import { ERA_IDS, type EraId, ERAS } from '../config/eras';
import { PHYSICS_RANGES, type PhysicsTuning } from '../config/tuning';
import { formatDate, isValidDate, shiftDate } from '../game/daily';
import type { GhostSource, Session } from '../game/session';
import type { GeneratedCircuit } from '../track/generate';
import { el, formatLap } from './format';

export type OverlayName = 'sectors' | 'curvature' | 'braking' | 'racingLine';

export interface DevHost {
  circuit(): GeneratedCircuit;
  session(): Session;
  loadDate(date: string, salt: number): void;
  /** The era being previewed instead of the day's car, or null. */
  eraOverride(): EraId | null;
  setEra(era: EraId | null): void;
  setOverlay(name: OverlayName, on: boolean): void;
  setShowFps(on: boolean): void;
  setGhostSource(src: GhostSource): void;
  setAutopilot(on: boolean): void;
  setPost(on: boolean): void;
  setHud(on: boolean): void;
  rendererInfo(): { calls: number; triangles: number; fps: number };
  onPhysicsChanged(): void;
  /** The car as it handles now (not as it did on the loaded day). */
  currentPhysics(): PhysicsTuning;
  replayBest(): { ok: boolean; detail: string } | null;
}

/** The F1 dev panel. */
export class DevPanel {
  readonly root: HTMLElement;
  private info: HTMLElement;
  private verify: HTMLElement;
  private perf: HTMLElement;
  private tunedWarn: HTMLElement;
  private sliders = new Map<keyof PhysicsTuning, { row: HTMLElement; input: HTMLInputElement; val: HTMLElement }>();
  private dateInput: HTMLInputElement;
  private eraSelect: HTMLSelectElement;
  private open = false;
  private handlingBox!: HTMLInputElement;
  private perfTimer = 0;
  state = {
    overlays: { sectors: false, curvature: false, braking: false, racingLine: false } as Record<OverlayName, boolean>,
    fps: false,
    autopilot: false,
    post: true,
    hud: true,
    ghost: 'best' as GhostSource,
    /** Drive past days with today's handling (practice: those laps aren't saved). */
    currentHandling: false,
  };

  constructor(parent: HTMLElement, private host: DevHost) {
    this.root = el('div', 'dev interactive', parent);
    // Typing in the panel shouldn't drive the car; F1 / backquote still closes it.
    this.root.addEventListener('keydown', (e) => {
      if (e.code !== 'F1' && e.code !== 'Backquote') e.stopPropagation();
    });

    el('h4', '', this.root, 'Circuit');
    const row = el('div', 'row', this.root);
    this.dateInput = el('input', '', row);
    this.dateInput.type = 'date';
    const load = el('button', '', row, 'Load');
    const prev = el('button', '', row, '◀');
    const next = el('button', '', row, '▶');
    const today = el('button', '', row, 'Today');
    const reroll = el('button', '', row, 'Reroll seed');
    load.onclick = () => isValidDate(this.dateInput.value) && this.host.loadDate(this.dateInput.value, 0);
    prev.onclick = () => this.host.loadDate(shiftDate(this.host.circuit().date, -1), 0);
    next.onclick = () => this.host.loadDate(shiftDate(this.host.circuit().date, 1), 0);
    today.onclick = () => this.host.loadDate('', 0);
    reroll.onclick = () => {
      const c = this.host.circuit();
      this.host.loadDate(c.date, c.salt + 1);
    };
    const carRow = el('div', 'row', this.root);
    el('span', '', carRow, 'Car:');
    this.eraSelect = el('select', '', carRow);
    const auto = el('option', '', this.eraSelect, "the day's car");
    auto.value = 'auto';
    for (const id of ERA_IDS) {
      const o = el('option', '', this.eraSelect, `${id} · ${ERAS[id].name}`);
      o.value = id;
    }
    this.eraSelect.onchange = () => this.host.setEra(this.eraSelect.value === 'auto' ? null : (this.eraSelect.value as EraId));
    this.info = el('div', 'kv', this.root);

    el('h4', '', this.root, 'Show');
    const toggles = el('div', '', this.root);
    const chk = (label: string, get: () => boolean, set: (v: boolean) => void): void => {
      const l = el('label', 'chk', toggles);
      const i = el('input', '', l);
      i.type = 'checkbox';
      i.checked = get();
      i.onchange = () => set(i.checked);
      el('span', '', l, label);
    };
    const names: [OverlayName, string][] = [
      ['sectors', 'Sectors (S1 red · S2 ochre · S3 green)'],
      ['curvature', 'Curvature (cream → red = tight)'],
      ['braking', 'Braking zones'],
      ['racingLine', 'Bot racing line'],
    ];
    for (const [k, label] of names) {
      chk(label, () => this.state.overlays[k], (v) => {
        this.state.overlays[k] = v;
        this.host.setOverlay(k, v);
      });
    }
    chk('FPS counter', () => this.state.fps, (v) => {
      this.state.fps = v;
      this.host.setShowFps(v);
    });
    chk('HUD', () => this.state.hud, (v) => {
      this.state.hud = v;
      this.host.setHud(v);
    });
    chk('Paper grain + vignette', () => this.state.post, (v) => {
      this.state.post = v;
      this.host.setPost(v);
    });
    chk('Autopilot (the bot drives)', () => this.state.autopilot, (v) => {
      this.state.autopilot = v;
      this.host.setAutopilot(v);
    });
    const grow = el('div', 'row', toggles);
    el('span', '', grow, 'Ghost:');
    const gsel = el('select', '', grow);
    for (const [v, l] of [['best', 'my best lap'], ['bot', 'bot (author) lap'], ['none', 'off']] as const) {
      const o = el('option', '', gsel, l);
      o.value = v;
    }
    gsel.onchange = () => {
      this.state.ghost = gsel.value as GhostSource;
      this.host.setGhostSource(this.state.ghost);
    };

    el('h4', '', this.root, 'Replay verification');
    this.verify = el('div', '', this.root);
    const vr = el('div', 'row', this.root);
    const rb = el('button', '', vr, 'Replay my best lap now');
    rb.onclick = () => {
      const r = this.host.replayBest();
      this.verify.innerHTML = r ? `<span class="${r.ok ? 'ok' : 'bad'}">${r.ok ? '✓' : '✗'} ${r.detail}</span>` : 'No best lap stored for this circuit yet.';
    };

    el('h4', '', this.root, 'Physics (live)');
    this.tunedWarn = el('div', 'warn', this.root, 'Tuning edited: laps are not saved as records.');
    this.tunedWarn.style.display = 'none';
    const reset = el('button', '', el('div', 'row', this.root), "Reset to the day's car");
    reset.onclick = () => {
      this.state.currentHandling = false;
      this.handlingBox.checked = false;
      Object.assign(this.host.session().physics, this.host.circuit().physics);
      this.syncSliders();
      this.host.onPhysicsChanged();
    };
    // Past days keep the handling their laps were set with; this drives one with today's instead.
    const hl = el('label', 'chk', el('div', 'row', this.root));
    this.handlingBox = el('input', '', hl);
    this.handlingBox.type = 'checkbox';
    el('span', '', hl, "Today's handling on past days (practice, not saved)");
    this.handlingBox.onchange = () => {
      this.state.currentHandling = this.handlingBox.checked;
      this.applyHandling();
    };
    for (const key of Object.keys(PHYSICS_RANGES) as (keyof PhysicsTuning)[]) {
      const [min, max, step] = PHYSICS_RANGES[key]!;
      const r = el('div', 'slider', this.root);
      el('span', '', r, key);
      const input = el('input', '', r);
      input.type = 'range';
      input.min = String(min);
      input.max = String(max);
      input.step = String(step);
      const val = el('span', '', r);
      input.oninput = () => {
        // Before the first day has loaded there's nothing to tune.
        const session: Session | undefined = this.host.session();
        if (!session) return;
        (session.physics as unknown as Record<string, number>)[key] = Number(input.value);
        this.syncSliders();
        this.host.onPhysicsChanged();
      };
      this.sliders.set(key, { row: r, input, val });
    }

    el('h4', '', this.root, 'Performance');
    this.perf = el('div', 'kv', this.root);
    el('div', 'small', this.root, 'Tip: ?date=YYYY-MM-DD opens any day, ?era=1972 previews another car.');
  }

  toggle(): void {
    this.open = !this.open;
    this.root.classList.toggle('open', this.open);
    if (this.open) this.refresh();
  }

  get isOpen(): boolean {
    return this.open;
  }

  private syncSliders(): void {
    const p = this.host.session().physics;
    let tuned = false;
    for (const [key, s] of this.sliders) {
      const v = p[key];
      s.input.value = String(v);
      s.val.textContent = Number(v).toFixed(Math.max(0, -Math.floor(Math.log10(Number(s.input.step) || 1))));
      const changed = v !== this.host.circuit().physics[key];
      s.row.classList.toggle('changed', changed);
      tuned ||= changed;
    }
    this.tunedWarn.style.display = tuned ? '' : 'none';
  }

  /** Put today's handling on the loaded day's car (or the day's own back), keeping the switch across day changes. */
  applyHandling(): void {
    const phys = this.state.currentHandling ? this.host.currentPhysics() : this.host.circuit().physics;
    Object.assign(this.host.session().physics, phys);
    this.syncSliders();
    this.host.onPhysicsChanged();
  }

  refresh(): void {
    // Closed, there's nothing to show (opening it refreshes it): it used to cost a few ms at every finish line.
    if (!this.open) return;
    const c = this.host.circuit();
    const t = c.track;
    const f = t.features;
    this.dateInput.value = c.date;
    const override = this.host.eraOverride();
    this.eraSelect.value = override ?? 'auto';
    const kv: [string, string][] = [
      ['name', c.name],
      ['car', `${ERAS[c.era].name}, ${c.era}${override ? ' (preview: laps saved separately)' : ''}`],
      ['date', formatDate(c.date)],
      ['salt / attempt', `${c.salt} / ${c.attempt} (${c.archetype} layout)`],
      ['generated in', `${c.stats.millis.toFixed(0)} ms, ${c.stats.attempts} attempts, ${c.stats.botRuns} bot laps`],
      ['rejected', Object.entries(c.stats.rejections).map(([k, v]) => `${k} ×${v}`).join(', ') || '—'],
      ['length', `${t.length.toFixed(0)} m, ${t.n} samples`],
      ['terrain', terrainLine(t.elev, t.length, t.terrain)],
      ['bot (author)', `${formatLap(c.medals.author)} at grip ${c.bot.gripLevel}`],
      ['gold / silver / bronze', `${formatLap(c.medals.gold)} / ${formatLap(c.medals.silver)} / ${formatLap(c.medals.bronze)}`],
      ['long straight', `${f.longStraight.length.toFixed(0)} m → braking −${f.heavyBraking.speedDrop.toFixed(0)} m/s`],
      ['hairpin', f.hairpin ? `${Math.abs((f.hairpin.angle * 180) / Math.PI).toFixed(0)}°, r ${f.hairpin.minRadius.toFixed(0)} m` : '—'],
      ['chicane', f.chicane ? `${Math.abs((f.chicane[0].angle * 180) / Math.PI).toFixed(0)}° / ${Math.abs((f.chicane[1].angle * 180) / Math.PI).toFixed(0)}°` : '—'],
      ['sweeper', f.sweeper ? `${Math.abs((f.sweeper.angle * 180) / Math.PI).toFixed(0)}°, r ≥ ${f.sweeper.minRadius.toFixed(0)} m` : '—'],
      ['esses', f.esses ? `${f.esses.length} bends, r ≥ ${Math.min(...f.esses.map((c) => c.minRadius)).toFixed(0)} m` : '—'],
      ['crossover', t.crossing ? `${((Math.asin(t.crossing.sin) * 180) / Math.PI).toFixed(0)}°, underpass ${t.crossing.depth.toFixed(1)} m deep` : '—'],
      ['braking zones', String(t.brakingZones.length)],
      ['track hash', c.trackHash],
    ];
    this.info.innerHTML = kv.map(([k, v]) => `<span>${k}</span><span>${v}</span>`).join('');
    const s = this.host.session();
    const lv = s.lastVerification;
    this.verify.innerHTML = lv ? `<span class="${lv.ok ? 'ok' : 'bad'}">${lv.ok ? '✓' : '✗'} last PB: ${lv.detail}</span>` : '<span class="small">Set a personal best to see its automatic replay check.</span>';
    this.syncSliders();
  }

  tick(dt: number): void {
    if (!this.open) return;
    this.perfTimer -= dt;
    if (this.perfTimer > 0) return;
    this.perfTimer = 0.5;
    const i = this.host.rendererInfo();
    const s = this.host.session();
    const c = s.state.car;
    this.perf.innerHTML = [
      ['fps', i.fps.toFixed(0)],
      ['draw calls', String(i.calls)],
      ['triangles', i.triangles.toLocaleString()],
      ['speed', `${(Math.hypot(c.vx, c.vy) * 3.6).toFixed(0)} km/h, gear ${c.gear}, ${c.rpm.toFixed(0)} rpm`],
      ['slip f / r', `${c.slipFront.toFixed(3)} / ${c.slipRear.toFixed(3)}`],
      ['track s / d', `${c.s.toFixed(1)} / ${c.d.toFixed(2)}`],
      ['tick', String(s.state.tick)],
    ]
      .map(([k, v]) => `<span>${k}</span><span>${v}</span>`)
      .join('');
  }
}

/** "spa: 16 m of climb, steepest 14%" */
function terrainLine(elev: number[], length: number, kind: string): string {
  if (kind === 'flat') return 'flat';
  const ds = length / elev.length;
  let steepest = 0;
  for (let i = 0; i < elev.length; i++) steepest = Math.max(steepest, Math.abs(elev[(i + 1) % elev.length] - elev[i]) / ds);
  return `${kind}: ${(Math.max(...elev) - Math.min(...elev)).toFixed(0)} m of height, steepest ${(steepest * 100).toFixed(0)}%`;
}
